import React, { useMemo, useState } from "react";
import { Bar } from "@reactchartjs/react-chart.js";
import { Box, IconButton, Paper, Typography } from "@material-ui/core";
import { withStyles } from "@material-ui/styles";
import { compose } from "recompose";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";

const WEEKS_PER_PAGE = 7;

const drawAnnotations = (chartInstance) => {
  const chart = chartInstance.chart || chartInstance;

  if (!chart || typeof chart.getDatasetMeta !== "function") {
    console.error(
      "Chart instance or getDatasetMeta method not found for annotation.",
    );
    return;
  }

  const ctx = chart.ctx;
  const barMeta = chart.getDatasetMeta(0);

  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  ctx.fillStyle = "#1976D2";
  ctx.font = "bold 12px Arial";

  const barData = chart.config.data.datasets[0].data;

  const formatValueRaw = (value) => {
    return value;
  };

  barMeta.data.forEach((point, index) => {
    const dataValue = barData[index];
    const label = formatValueRaw(dataValue);

    ctx.fillText(label, point._model.x, point._model.y - 5);
  });
  ctx.restore();
};

// --- Bar Graph View Component ---

function BarGraphView({ classes, data, challengeParameters }) {
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = Math.ceil((data?.length || 0) / WEEKS_PER_PAGE);

  const chartData = useMemo(() => {
    const startIndex = currentPage * WEEKS_PER_PAGE;
    const endIndex = startIndex + WEEKS_PER_PAGE;
    const slice = Array.isArray(data) ? data.slice(startIndex, endIndex) : [];

    if (slice.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = slice.map((item) => {
      const date = new Date(item.activityDate);
      if (isNaN(date)) return item.activityDate;
      return `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
        .getDate()
        .toString()
        .padStart(2, "0")}`;
    });

    const values = slice.map((item) => item.totalValue);

    return {
      labels: labels,
      datasets: [
        {
          type: "line",
          label: "Total Value",
          data: values,
          fill: false,
          borderColor: "#1976D2",
          backgroundColor: "#1976D2",
          pointRadius: 6,
          yAxisID: "y-axis-1",
        },
        {
          type: "bar",
          label: "Total Value",
          borderColor: "#d28819ff",
          backgroundColor: "#d28819ff",
          data: values,
        },
      ],
    };
  }, [data, currentPage]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
      xAxes: [{ gridLines: { display: false }, maxBarThickness: 50 }],
      yAxes: [
        {
          id: "y-axis-1",
          type: "linear",
          position: "left",
          ticks: {
            beginAtZero: true,
            callback: function (value, index, values) {
              return new Intl.NumberFormat().format(value);
            },
          },
          gridLines: { color: "#e0e0e0" },
        },
      ],
    },
    tooltips: {
      enabled: true,
      filter: function (tooltipItem, data) {
        return tooltipItem.datasetIndex === 0;
      },
      callbacks: {
        label: function (tooltipItem, data) {
          const value = tooltipItem.yLabel;
          const label =
            challengeParameters === "STEPS"
              ? "Steps"
              : challengeParameters === "DISTANCE"
                ? "Distance"
                : challengeParameters === "PROGRAM_CHALLENGE"
                  ? "Points"
                  : "Value";
          return `${label}: ${value}`;
        },
      },
    },
    animation: {
      duration: 0,
      onComplete: drawAnnotations,
    },
    layout: {
    padding: {
      top: 30,
      bottom: 0
    }
  },
  };

  // --- Pagination Handlers ---
  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (!data || data.length === 0) {
    return <Typography color="textSecondary">No data available.</Typography>;
  }

  return (
    <>
      <IconButton
        onClick={handlePrevious}
        disabled={currentPage === 0}
        size="small"
        className={classes.navButton}
      >
        <ArrowBackIosIcon style={{ fontSize: 16 }} />
      </IconButton>

      <Box className={classes.chartWrapper}>
        <Bar data={chartData} options={options} />
      </Box>

      <IconButton
        onClick={handleNext}
        disabled={currentPage >= totalPages - 1}
        size="small"
        className={classes.navButton}
      >
        <ArrowForwardIosIcon style={{ fontSize: 16 }} />
      </IconButton>
    </>
  );
}

const styles = (theme) => ({
  chartWrapper: {
    flexGrow: 1,
    position: "relative",
    height: "300px",
  },
  navButton: {
    fontSize: "16px",
    margin: "0 5px",
    backgroundColor: "#000",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#333 !important",
    },
  },
});

export default compose(withStyles(styles, { withTheme: true }))(BarGraphView);
